import { useEffect } from "react";
import { PublicLayout } from "@/components/layout/PublicLayout";
import { LEGAL } from "@/config/legal";

const Privacy = () => {
  useEffect(() => {
    document.title = "Privacy Policy | Decision Sales OS";
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute("content", "Privacy Policy for Decision Sales OS. Learn how we collect, use, and protect your data.");
    }
  }, []);

  return (
    <PublicLayout showCookieBanner={false}>
      <h1 className="text-3xl font-semibold tracking-tight mb-4">Privacy Policy</h1>
      <p className="text-muted-foreground mb-10">Last updated: December 2024</p>
      <div className="prose prose-neutral dark:prose-invert max-w-none">
      <section className="mb-10">
        <h2 className="text-xl font-semibold text-foreground mb-4">1. Introduction</h2>
        <p className="text-muted-foreground leading-relaxed mb-4">
          Decision Sales OS ("we," "our," or "us") is committed to protecting your privacy. This 
          Privacy Policy explains how we collect, use, disclose, and safeguard your information 
          when you use our sales operating system platform.
        </p>
        <p className="text-muted-foreground leading-relaxed">
          By accessing or using Decision Sales OS, you agree to the terms of this Privacy Policy. 
          If you do not agree with our policies and practices, please do not use our service.
        </p>
      </section>

      <section className="mb-10">
        <h2 className="text-xl font-semibold text-foreground mb-4">2. Information We Collect</h2>
        <h3 className="font-medium text-foreground mb-3 mt-6">2.1 Information You Provide</h3>
        <ul className="space-y-2 text-muted-foreground mb-4">
          <li className="flex items-start gap-3">
            <span className="w-1.5 h-1.5 rounded-full bg-muted-foreground mt-2 flex-shrink-0" />
            <span>Account information: name, email address, password, and company details</span>
          </li>
          <li className="flex items-start gap-3">
            <span className="w-1.5 h-1.5 rounded-full bg-muted-foreground mt-2 flex-shrink-0" />
            <span>Lead and contact data: names, emails, phone numbers, company information</span>
          </li>
          <li className="flex items-start gap-3">
            <span className="w-1.5 h-1.5 rounded-full bg-muted-foreground mt-2 flex-shrink-0" />
            <span>Deal and pipeline information: values, stages, notes, and related activities</span>
          </li>
          <li className="flex items-start gap-3">
            <span className="w-1.5 h-1.5 rounded-full bg-muted-foreground mt-2 flex-shrink-0" />
            <span>Calendar and task data: events, reminders, and scheduled activities</span>
          </li>
        </ul>
        <h3 className="font-medium text-foreground mb-3">2.2 Automatically Collected Information</h3>
        <ul className="space-y-2 text-muted-foreground">
          <li className="flex items-start gap-3">
            <span className="w-1.5 h-1.5 rounded-full bg-muted-foreground mt-2 flex-shrink-0" />
            <span>Usage data: features accessed, actions taken, time spent</span>
          </li>
          <li className="flex items-start gap-3">
            <span className="w-1.5 h-1.5 rounded-full bg-muted-foreground mt-2 flex-shrink-0" />
            <span>Device information: browser type, operating system, device identifiers</span>
          </li>
          <li className="flex items-start gap-3">
            <span className="w-1.5 h-1.5 rounded-full bg-muted-foreground mt-2 flex-shrink-0" />
            <span>Log data: IP addresses, access times, pages viewed</span>
          </li>
        </ul>
      </section>

      <section className="mb-10">
        <h2 className="text-xl font-semibold text-foreground mb-4">3. How We Use Your Information</h2>
        <p className="text-muted-foreground leading-relaxed mb-4">We use the information we collect to:</p>
        <ul className="space-y-2 text-muted-foreground">
          <li className="flex items-start gap-3">
            <span className="w-1.5 h-1.5 rounded-full bg-muted-foreground mt-2 flex-shrink-0" />
            <span>Provide, maintain, and improve Decision Sales OS</span>
          </li>
          <li className="flex items-start gap-3">
            <span className="w-1.5 h-1.5 rounded-full bg-muted-foreground mt-2 flex-shrink-0" />
            <span>Process your requests and respond to your inquiries</span>
          </li>
          <li className="flex items-start gap-3">
            <span className="w-1.5 h-1.5 rounded-full bg-muted-foreground mt-2 flex-shrink-0" />
            <span>Send administrative information, such as updates and security alerts</span>
          </li>
          <li className="flex items-start gap-3">
            <span className="w-1.5 h-1.5 rounded-full bg-muted-foreground mt-2 flex-shrink-0" />
            <span>Analyze usage patterns to enhance user experience</span>
          </li>
          <li className="flex items-start gap-3">
            <span className="w-1.5 h-1.5 rounded-full bg-muted-foreground mt-2 flex-shrink-0" />
            <span>Comply with legal obligations and protect our rights</span>
          </li>
        </ul>
      </section>

      <section className="mb-10">
        <h2 className="text-xl font-semibold text-foreground mb-4">4. Data Security</h2>
        <p className="text-muted-foreground leading-relaxed mb-4">
          We implement appropriate technical and organizational security measures to protect your 
          information. These include:
        </p>
        <ul className="space-y-2 text-muted-foreground">
          <li className="flex items-start gap-3">
            <span className="w-1.5 h-1.5 rounded-full bg-muted-foreground mt-2 flex-shrink-0" />
            <span>Encryption of data in transit using TLS/SSL</span>
          </li>
          <li className="flex items-start gap-3">
            <span className="w-1.5 h-1.5 rounded-full bg-muted-foreground mt-2 flex-shrink-0" />
            <span>Encryption of data at rest</span>
          </li>
          <li className="flex items-start gap-3">
            <span className="w-1.5 h-1.5 rounded-full bg-muted-foreground mt-2 flex-shrink-0" />
            <span>Role-based access controls</span>
          </li>
          <li className="flex items-start gap-3">
            <span className="w-1.5 h-1.5 rounded-full bg-muted-foreground mt-2 flex-shrink-0" />
            <span>Regular security assessments and monitoring</span>
          </li>
        </ul>
      </section>

      <section className="mb-10">
        <h2 className="text-xl font-semibold text-foreground mb-4">5. Data Retention</h2>
        <p className="text-muted-foreground leading-relaxed">
          We retain your information for as long as your account is active or as needed to provide 
          you with our services. Upon account termination, we will delete or anonymize your data 
          within 90 days, unless retention is required by law or for legitimate business purposes.
        </p>
      </section>

      <section className="mb-10">
        <h2 className="text-xl font-semibold text-foreground mb-4">6. Your Rights</h2>
        <p className="text-muted-foreground leading-relaxed mb-4">
          Depending on your location, you may have certain rights regarding your personal information:
        </p>
        <ul className="space-y-2 text-muted-foreground">
          <li className="flex items-start gap-3">
            <span className="w-1.5 h-1.5 rounded-full bg-muted-foreground mt-2 flex-shrink-0" />
            <span>Access: Request a copy of your personal data</span>
          </li>
          <li className="flex items-start gap-3">
            <span className="w-1.5 h-1.5 rounded-full bg-muted-foreground mt-2 flex-shrink-0" />
            <span>Correction: Request correction of inaccurate data</span>
          </li>
          <li className="flex items-start gap-3">
            <span className="w-1.5 h-1.5 rounded-full bg-muted-foreground mt-2 flex-shrink-0" />
            <span>Deletion: Request deletion of your personal data</span>
          </li>
          <li className="flex items-start gap-3">
            <span className="w-1.5 h-1.5 rounded-full bg-muted-foreground mt-2 flex-shrink-0" />
            <span>Portability: Request transfer of your data to another service</span>
          </li>
        </ul>
      </section>

      <section className="mb-10">
        <h2 className="text-xl font-semibold text-foreground mb-4">7. Third-Party Services</h2>
        <p className="text-muted-foreground leading-relaxed">
          Decision Sales OS may integrate with third-party services to provide enhanced functionality. 
          These integrations are optional and governed by their respective privacy policies. We 
          encourage you to review the privacy practices of any third-party services you choose to connect.
        </p>
      </section>

      <section>
        <h2 className="text-xl font-semibold text-foreground mb-4">8. Contact Us</h2>
        <p className="text-muted-foreground leading-relaxed">
          If you have questions about this Privacy Policy or wish to exercise your rights, 
          please contact our DPO at{" "}
          <a 
            href={`mailto:${LEGAL.dpoEmail}`} 
            className="text-primary hover:text-primary/80 transition-colors"
          >
            {LEGAL.dpoEmail}
          </a>
          . {LEGAL.companyName}, {LEGAL.address}. P.IVA/VAT: {LEGAL.pivaVat}.
        </p>
      </section>
      </div>
    </PublicLayout>
  );
};

export default Privacy;
